<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { computed, ref } from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormResult,
  FormSchema,
  FormSchemaErrors,
} from '@modules/forms/components/VForm/types';
import type {
  ISettingsEmailsItemPayload,
  ISettingsEmailsItemSavePayload,
} from '@modules/general-settings/submodules/emails/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import { TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS } from '@modules/general-settings/submodules/emails/constants';
import { ESettingsEmailsField } from '@modules/general-settings/submodules/emails/enums';
import useSettingsRegistrationFieldsAddMutation
  from '@modules/general-settings/submodules/emails/mutations/useSettingsEmailItemSaveMutation';
import { useSettingsEmailTriggersListQuery } from '@modules/general-settings/submodules/emails/queries';
import useSettingsEmailItemQuery from '@modules/general-settings/submodules/emails/queries/useSettingsEmailItemQuery';
import { useI18n, useLanguages } from '@modules/i18n/composables';
import { useI18nStore } from '@modules/i18n/store';

const emailItemPayload = ref<ISettingsEmailsItemPayload>();

const { data: triggersList, isLoading } = useSettingsEmailTriggersListQuery();
const { data: emailItem, isLoading: isLoadingEmailItem } = useSettingsEmailItemQuery(emailItemPayload);

const { mutateAsync: saveEmailItem, errors: rawErrors, clearError } = useSettingsRegistrationFieldsAddMutation();

const { t } = useTranslation();
const { interfaceLanguages } = useLanguages();
const { getTranslation } = useI18n();
const { currentLocale } = useI18nStore();

const mailerList = computed(() => Object.keys(triggersList.value?.list ?? {})?.map((key) => ({
  label: t(`${triggersList.value?.translate_namespace}${key}`),
  value: key,
})) || []);

const editorVariables = computed(() => {
  const index = emailItemPayload.value?.[ESettingsEmailsField.ID_MAILER];
  return index ? triggersList.value?.list[index] : [];
});

const canBeDisabled = computed(() => triggersList.value?.disableable_list.includes(
  emailItemPayload.value?.id_mailer || '',
) ?? false);

const errors = computed<FormSchemaErrors<ISettingsEmailsItemSavePayload, never> | undefined>(() => ({
  ...rawErrors.value,
  [ESettingsEmailsField.PREFIX_MAILER]: {
    [ESettingsEmailsField.BODY]: [
      ...rawErrors.value?.body?.map((item) => ({
        ...item,
        locale_key: rawErrors.value?.variable?.length ? `${item.locale_key} ${rawErrors.value?.variable.join(', ')}` : item.locale_key,
      })) ?? [],
    ],
    [ESettingsEmailsField.SUBJECT]: [
      ...rawErrors.value?.subject ?? [],
    ],
  },
}));

const schemaValues = ref<ISettingsEmailsItemSavePayload>();

const formSchema = computed<FormSchema<ISettingsEmailsItemSavePayload>>(() => ({
  locale: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'LANGUAGE').value,
    options: interfaceLanguages.value,
    value: currentLocale,
  },
  id_mailer: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'EMAILS_LIST').value,
    options: mailerList.value,
    required: true,
  },
  mailer: {
    subject: {
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'SUBJECT').value,
      type: EFormInputTypes.TEXT,
      value: schemaValues.value?.mailer.subject ?? emailItem.value?.mailer.subject,
      required: true,
    },
    body: {
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'BODY').value,
      type: EFormInputTypes.TEXT_EDITOR,
      value: schemaValues.value?.mailer.body ?? emailItem.value?.mailer.body,
      variables: editorVariables.value,
      required: true,
    },
    enabled: {
      label: getTranslation(
        TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS,
        canBeDisabled.value ? 'ENABLE_SENDING_EMAIL' : 'EMAIL_CANNOT_BE_DISABLED',
      ).value,
      type: canBeDisabled.value ? EFormInputTypes.CHECKBOX : EFormInputTypes.HIDDEN,
      icon: canBeDisabled.value ? '' : 'pi-exclamation-circle',
      value: schemaValues.value?.mailer.enabled ?? emailItem.value?.mailer.enabled,
      required: true,
    },
  },
}));

const groups = computed<FormGroup<ISettingsEmailsItemSavePayload>[]>(() => {
  const res: FormGroup<ISettingsEmailsItemSavePayload>[] = [{
    items: ['locale', 'id_mailer'],
  }];

  if (emailItemPayload.value) {
    const secondPart: FormGroup<ISettingsEmailsItemSavePayload> = {
      title: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_EMAILS, 'EMAIL_SETTINGS').value,
      items: ['mailer.subject', 'mailer.body', 'mailer.enabled'],
      isLoading: isLoadingEmailItem.value,
      minHeight: '400px',
    };

    res.push(secondPart);
  }

  return res;
});

function handleChange<
  T extends ISettingsEmailsItemSavePayload,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, result: FormResult<T>): void {
  if (key === ESettingsEmailsField.LOCALE || key === ESettingsEmailsField.ID_MAILER) {
    schemaValues.value = undefined;
    emailItemPayload.value = {
      locale: result.values.locale,
      id_mailer: result.values.id_mailer,
    };
  } else {
    schemaValues.value = result.values;
  }
}

function handleInteracted(key: FormFlattenKey<ISettingsEmailsItemSavePayload>): void {
  switch (key) {
    case 'mailer.subject':
      clearError(ESettingsEmailsField.SUBJECT);
      break;
    case 'mailer.body':
      clearError(ESettingsEmailsField.BODY);
      clearError('variable');
      break;
    case 'mailer.enabled':
      clearError(ESettingsEmailsField.ENABLED);
      break;
    default:
      clearError(key);
  }
}
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsEmailsPage'">
    <VLoader
      :loading="isLoading"
      min-height="350px"
    >
      <VForm
        :schema="formSchema"
        :groups="groups"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :custom-errors="errors"
        @change="handleChange"
        @submit="saveEmailItem"
        @interacted="handleInteracted"
      />
    </VLoader>
  </VCard>
</template>
